/**
 * ClientExecutionFitForm Component
 * Execution fit assessment form with checkbox and radio fields
 */

import React, { useState, FormEvent } from 'react';
import { toast } from 'react-hot-toast';

interface FormData {
  name: string;
  email: string;
  consultingWork: string[];
  executionBreakdown: string[];
  organizationSize: string;
  executionTracking: string[];
  engagementStart: string;
  explorationPrompt: string;
}

export const ClientExecutionFitForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    consultingWork: [],
    executionBreakdown: [],
    organizationSize: '',
    executionTracking: [],
    engagementStart: '',
    explorationPrompt: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Checkbox handlers
  const handleCheckboxChange = (field: 'consultingWork' | 'executionBreakdown' | 'executionTracking', value: string) => {
    setFormData(prev => {
      const currentValues = prev[field];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [field]: newValues };
    });
  };

  // Radio handlers
  const handleRadioChange = (field: 'organizationSize' | 'engagementStart' | 'explorationPrompt', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Text input handler
  const handleInputChange = (field: 'name' | 'email', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    if (formData.consultingWork.length === 0) {
      toast.error('Please select at least one consulting work type');
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Replace with actual API endpoint
      console.log('Form submission:', formData);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      toast.success('Thank you! We\'ll be in touch soon to discuss execution fit.');

      // Reset form
      setFormData({
        name: '',
        email: '',
        consultingWork: [],
        executionBreakdown: [],
        organizationSize: '',
        executionTracking: [],
        engagementStart: '',
        explorationPrompt: ''
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="execution-fit-form" className="bg-white py-20 md:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">

          {/* Form Header */}
          <div className="mb-12 text-center">
            <h2 className="font-display text-4xl md:text-5xl text-stone-900 mb-4">
              Check execution fit
            </h2>
            <p className="text-lg text-stone-700 leading-relaxed">
              This helps us understand how you work and whether OKR Hub fits your execution approach.
              <br />
              <span className="text-stone-900 font-medium">No sales pitch. No obligation.</span>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">

            {/* 1. Name */}
            <div>
              <label htmlFor="name" className="block text-lg font-medium text-stone-900 mb-3">
                Your name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="name"
                required
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-3 border-2 border-stone-300 rounded-lg focus:border-stone-900 focus:ring-2 focus:ring-stone-900 focus:ring-opacity-20 transition-colors text-stone-900"
                placeholder="Your full name"
              />
            </div>

            {/* 2. Email */}
            <div>
              <label htmlFor="email" className="block text-lg font-medium text-stone-900 mb-3">
                Email address <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                id="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-4 py-3 border-2 border-stone-300 rounded-lg focus:border-stone-900 focus:ring-2 focus:ring-stone-900 focus:ring-opacity-20 transition-colors text-stone-900"
                placeholder="your@email.com"
              />
            </div>

            {/* 3. Consulting Work - Checkboxes */}
            <div>
              <label className="block text-lg font-medium text-stone-900 mb-3">
                How would you describe your consulting work? <span className="text-red-600">*</span>
              </label>
              <p className="text-sm text-stone-600 mb-4">Select all that apply</p>
              <div className="space-y-3">
                {[
                  'Strategy execution',
                  'Leadership alignment',
                  'Business / org transformation',
                  'Performance management',
                  'HR / people advisory',
                  'Fractional leadership role',
                  'Other'
                ].map((option) => (
                  <label key={option} className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.consultingWork.includes(option)}
                      onChange={() => handleCheckboxChange('consultingWork', option)}
                      className="mt-1 w-5 h-5 text-stone-900 border-2 border-stone-300 rounded focus:ring-2 focus:ring-stone-900 focus:ring-opacity-20 cursor-pointer"
                    />
                    <span className="ml-3 text-base text-stone-700 group-hover:text-stone-900">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 4. Execution Breakdown - Checkboxes */}
            <div>
              <label className="block text-lg font-medium text-stone-900 mb-3">
                What typically breaks down after your engagements end?
              </label>
              <p className="text-sm text-stone-600 mb-4">Select all that apply</p>
              <div className="space-y-3">
                {[
                  'Follow-through on priorities',
                  'Visibility into execution',
                  'Consistent review cadence',
                  'Accountability across teams',
                  'Progress depends on manual follow-ups',
                  'Nothing — execution usually sustains well'
                ].map((option) => (
                  <label key={option} className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.executionBreakdown.includes(option)}
                      onChange={() => handleCheckboxChange('executionBreakdown', option)}
                      className="mt-1 w-5 h-5 text-stone-900 border-2 border-stone-300 rounded focus:ring-2 focus:ring-stone-900 focus:ring-opacity-20 cursor-pointer"
                    />
                    <span className="ml-3 text-base text-stone-700 group-hover:text-stone-900">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 5. Organization Size - Radio */}
            <div>
              <label className="block text-lg font-medium text-stone-900 mb-3">
                What type of organizations do you primarily work with?
              </label>
              <div className="space-y-3">
                {[
                  '50–100 employees',
                  '100–500 employees',
                  '500–1500 employees',
                  '1500+ employees'
                ].map((option) => (
                  <label key={option} className="flex items-start cursor-pointer group">
                    <input
                      type="radio"
                      name="organizationSize"
                      checked={formData.organizationSize === option}
                      onChange={() => handleRadioChange('organizationSize', option)}
                      className="mt-1 w-5 h-5 text-stone-900 border-2 border-stone-300 focus:ring-2 focus:ring-stone-900 focus:ring-opacity-20 cursor-pointer"
                    />
                    <span className="ml-3 text-base text-stone-700 group-hover:text-stone-900">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 6. Execution Tracking - Checkboxes */}
            <div>
              <label className="block text-lg font-medium text-stone-900 mb-3">
                How is execution currently tracked with most clients?
              </label>
              <p className="text-sm text-stone-600 mb-4">Select all that apply</p>
              <div className="space-y-3">
                {[
                  'Spreadsheets',
                  'Slides / decks',
                  'Email or messaging updates',
                  'Review meetings only',
                  'A dedicated system or tool'
                ].map((option) => (
                  <label key={option} className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={formData.executionTracking.includes(option)}
                      onChange={() => handleCheckboxChange('executionTracking', option)}
                      className="mt-1 w-5 h-5 text-stone-900 border-2 border-stone-300 rounded focus:ring-2 focus:ring-stone-900 focus:ring-opacity-20 cursor-pointer"
                    />
                    <span className="ml-3 text-base text-stone-700 group-hover:text-stone-900">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 7. Engagement Start - Radio */}
            <div>
              <label className="block text-lg font-medium text-stone-900 mb-3">
                How do your engagements usually start?
              </label>
              <div className="space-y-3">
                {[
                  'Workshops',
                  'Small group leadership sessions',
                  'One-on-one discussions',
                  'Ongoing advisory / retainer',
                  'Mixed formats'
                ].map((option) => (
                  <label key={option} className="flex items-start cursor-pointer group">
                    <input
                      type="radio"
                      name="engagementStart"
                      checked={formData.engagementStart === option}
                      onChange={() => handleRadioChange('engagementStart', option)}
                      className="mt-1 w-5 h-5 text-stone-900 border-2 border-stone-300 focus:ring-2 focus:ring-stone-900 focus:ring-opacity-20 cursor-pointer"
                    />
                    <span className="ml-3 text-base text-stone-700 group-hover:text-stone-900">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* 8. Exploration Prompt - Radio */}
            <div>
              <label className="block text-lg font-medium text-stone-900 mb-3">
                What prompted you to explore execution infrastructure now?
              </label>
              <div className="space-y-3">
                {[
                  'Execution decay after engagements',
                  'Leadership asking for better visibility',
                  'Scaling client work is becoming hard',
                  'Looking to standardize execution across clients',
                  'General exploration'
                ].map((option) => (
                  <label key={option} className="flex items-start cursor-pointer group">
                    <input
                      type="radio"
                      name="explorationPrompt"
                      checked={formData.explorationPrompt === option}
                      onChange={() => handleRadioChange('explorationPrompt', option)}
                      className="mt-1 w-5 h-5 text-stone-900 border-2 border-stone-300 focus:ring-2 focus:ring-stone-900 focus:ring-opacity-20 cursor-pointer"
                    />
                    <span className="ml-3 text-base text-stone-700 group-hover:text-stone-900">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 text-lg font-medium text-white bg-stone-900 rounded-lg hover:bg-stone-800 transition-all duration-300 hover:-translate-y-0.5 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isSubmitting ? 'Submitting...' : 'Start a conversation about execution fit'}
              </button>
            </div>

          </form>

        </div>
      </div>
    </section>
  );
};
